import React, { Component } from "react";
import Helmet from "react-helmet";
import { Card, Layout, Tabs, Select, Divider } from "antd";
const { Sider, Content } = Layout;
import { withUser } from "@unifiedpatents/sso";
import JCTVCToolbar from "./repo/toolbar-hevc";
import RepoTable from "./repo/table";
import AppliedFilters from "../common/applied-filters";
import CallToLogin from "../common/call-to-login";
import CallToUpgrade from "../common/call-to-upgrade";
import { PUBLIC_ASSETS_URL } from "../../config";
import { withRouter } from "react-router";
import { keepOnlyJCTVCQueryStrings, mapJCTVCQueryStringToName } from "../../helpers/url";
import { API_URL } from "../../config";
import { omitBy, isEmpty, omit, isArray } from "lodash";
import { toSetString, toDateString } from "../../helpers/converters";
import DateHistogram from "./repo/date-histogram";
import FieldAggregation from "./repo/field-agg";

const styles = {
  body: {
    width: "100%",
    padding: 30
  },
  verticalGutter: {
    marginBottom: 16
  }
};

class RepoJCTVC extends Component {
  state = {
    activeTab: "list",
    fieldCategory: "source",
    fieldDisplaySize: 20,
    fieldDate: "upload_date",
    fieldDrilldownCategory: "standard",
    fieldDrilldownDate: "standard"
  };

  render() {
    const { activeTab } = this.state;
    const {
      status: { isLoggedIn },
      metadata
    } = this.props.user;
    const accessLevel = metadata.authorization.roles.indexOf("member-hevc-submission-repo") >= 0 ? "full" : isLoggedIn ? "guest" : "public";
    const helmet = {
      title: "HEVC Submissions Repository",
      meta: [
        {
          name: "keywords",
          content:
            "technical standards, standard setting body, standard setting organization, SSO, ITU, ITU-T, JCTVC, JVT, VCEG, joint collaborative team on video coding, joint video team, video coding experts group, ETSI, 3GPP, H.265, H.264, HEVC, AVC, high efficiency video coding, advanced video codec"
        },
        {
          name: "description",
          content: "This tool provides full-text search of the entire collection of JCTVC meeting notes. Use the database to complete analysis of HEVC & AVC standards specifications."
        }
      ]
    };
    const callToLoginAlert = isLoggedIn ? accessLevel === "full" ? null : <CallToUpgrade style={styles.verticalGutter} /> : <CallToLogin style={styles.verticalGutter} />;
    const filter = this._getFilter(accessLevel);
    const esUrl = `${API_URL}/repos/jvt,jctvc,jvet/_search`;
    const renderHyperLink = (text, record) => <a href={`https://s3-us-west-1.amazonaws.com/sso-document-repository/${record.s3_key_for_file}`}>{text}</a>;
    const allCols = [
      { title: "Document ID", dataIndex: "doc_id", width: 140, render: renderHyperLink },
      { title: "Version", dataIndex: "version", width: 90 },
      { title: "Title", dataIndex: "title", width: 600 },
      { title: "Status", dataIndex: "status", width: 220 },
      { title: "Purpose", dataIndex: "purpose", width: 130 },
      { title: "Meeting", dataIndex: "formatted_meeting_string", key: "formatted_meeting_string", width: 220, sorter: true },
      { title: "Authors", dataIndex: "authors", width: 200, render: toSetString },
      { title: "Company", dataIndex: "source", width: 200, render: toSetString },
      { title: "Date", dataIndex: "upload_date", key: "upload_date", width: 130, render: toDateString, sorter: true }
    ];
    const selectedColumns = ["doc_id", "version", "title", "status", "purpose", "formatted_meeting_string", "authors", "source", "upload_date"];
    return (
      <div style={styles.body}>
        <Helmet {...helmet} />
        {callToLoginAlert}
        <Layout>
          <Sider width={260} breakpoint="lg" collapsedWidth="0" style={{ background: "transparent", marginRight: 16 }}>
            <Card style={styles.verticalGutter} bodyStyle={{ textAlign: "center" }}>
              <img alt="OPEN Logo" src={`${PUBLIC_ASSETS_URL}/open_logo.png`} height={45} style={{ marginBottom: 8 }} />
              <br />
              <a href="https://support.unifiedpatents.com/hc/en-us/articles/360007551814-Video-Codec-Zone-JCT-VC-Repository" target="_new">
                Learn More
              </a>
            </Card>
            <JCTVCToolbar accessLevel={accessLevel} esUrl={esUrl} />
          </Sider>
          <Content>
            <AppliedFilters fieldSelector={keepOnlyJCTVCQueryStrings} fieldMapper={mapJCTVCQueryStringToName} omitQueryString={["sort", "documents"]} />
            <Tabs size="small" activeKey={activeTab} onChange={val => this.setState({ activeTab: val })} tabBarStyle={{ marginBottom: 0 }}>
              <Tabs.TabPane tab="Doc List" key="list" />
              <Tabs.TabPane tab="Analytics" key="analytics" disabled={false} />
            </Tabs>
            {activeTab === "list" && (
              <RepoTable
                filter={this._getFilterForES(filter)}
                accessLevel={accessLevel}
                esUrl={esUrl}
                allCols={allCols}
                selectedColumns={selectedColumns}
                defaultSort="-upload_date"
                tableKey="repoHEVC"
                rowKey="doc_id"
                key={JSON.stringify(filter)}
              />
            )}
            {activeTab === "analytics" && (
              <Card>
                <div style={{ textAlign: "center" }}>
                  <Select
                    value={this.state.fieldCategory}
                    placeholder="Aggregate By"
                    onChange={val => this.setState({ fieldCategory: val })}
                    getPopupContainer={trigger => trigger.parentNode}
                    style={{ width: 160 }}
                  >
                    {[
                      { value: "source", label: "Company" },
                      { value: "first_source", label: "Lead Company" },
                      { value: "formatted_meeting_string", label: "Meeting" }
                    ].map((e, i) => (
                      <Select.Option value={e.value} key={i}>
                        {e.label}
                      </Select.Option>
                    ))}
                  </Select>
                  <Divider type="vertical" />
                  <Select
                    value={this.state.fieldDrilldownCategory}
                    placeholder="Drill-Down By"
                    onChange={val => this.setState({ fieldDrilldownCategory: val })}
                    getPopupContainer={trigger => trigger.parentNode}
                    style={{ width: 160 }}
                  >
                    {[
                      { value: "", label: "None" },
                      { value: "simplified_status", label: "Approval" },
                      { value: "source", label: "Company" },
                      { value: "formatted_meeting_string", label: "Meeting" },
                      { value: "standard", label: "Standard" },
                      { value: "status", label: "Status" }
                    ].map((e, i) => (
                      <Select.Option value={e.value} key={i}>
                        {e.label}
                      </Select.Option>
                    ))}
                  </Select>
                  <Divider type="vertical" />
                  <Select
                    value={this.state.fieldDisplaySize}
                    placeholder="Display Size"
                    onChange={val => this.setState({ fieldDisplaySize: val })}
                    getPopupContainer={trigger => trigger.parentNode}
                    style={{ width: 130 }}
                  >
                    {[20, 50, 100].map(e => (
                      <Select.Option value={e} key={e}>{`Show Top ${e}`}</Select.Option>
                    ))}
                  </Select>
                  <FieldAggregation
                    field={this.state.fieldCategory}
                    field2={this.state.fieldDrilldownCategory}
                    size={this.state.fieldDisplaySize}
                    filter={this._getFilterForES(filter)}
                    fieldsWideLabel={["formatted_meeting_string"]}
                    esUrl={esUrl}
                  />
                </div>
                <Divider />
                <div style={{ textAlign: "center" }}>
                  <Select value={this.state.fieldDate} onChange={val => this.setState({ fieldDate: val })} getPopupContainer={trigger => trigger.parentNode} style={{ width: 180 }}>
                    <Select.Option value="upload_date">{"Submission Date"}</Select.Option>
                  </Select>
                  <Divider type="vertical" />
                  <Select
                    value={this.state.fieldDrilldownDate}
                    placeholder="Drill-Down By"
                    onChange={val => this.setState({ fieldDrilldownDate: val })}
                    getPopupContainer={trigger => trigger.parentNode}
                    style={{ width: 180 }}
                  >
                    {[
                      { value: "", label: "None" },
                      { value: "source", label: "Company" },
                      { value: "first_source", label: "Lead Company" },
                      { value: "standard", label: "Standard" },
                      { value: "status", label: "Status" }
                    ].map((e, i) => (
                      <Select.Option value={e.value} key={i}>
                        {e.label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <DateHistogram chartType={"LineChart"} field={this.state.fieldDate} field2={this.state.fieldDrilldownDate} filter={this._getFilterForES(filter)} esUrl={esUrl} axisTypeX={"linear"} />
              </Card>
            )}
          </Content>
        </Layout>
      </div>
    );
  }

  _getFilterForES = filter => {
    let newFilter = { ...filter };
    if (newFilter.meeting) {
      newFilter.formatted_meeting_string = newFilter.meeting;
    }
    if (newFilter.documents === "latest") {
      newFilter.is_latest = "true";
    }
    if (newFilter.documents === "cited") {
      newFilter.incorporated = "true";
    }

    return omit(newFilter, ["meeting", "documents"]);
  };

  _getFilter = accessLevel => {
    let query = keepOnlyJCTVCQueryStrings(this.props.location.query);
    query.standard = query.standard ? (isArray(query.standard) ? query.standard : [query.standard]) : ["AVC", "HEVC", "VVC"];
    query.documents = query.documents ? query.documents : "latest";
    if (accessLevel !== "full") {
      if (query.full_text && query.full_text != "example") {
        query.full_text = "example";
      }
    }
    return omitBy(query, isEmpty);
  };
}

export default withUser(withRouter(RepoJCTVC));
